import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ConsumeCheckData, ConsumeRaidNight, ConsumeRaidView, ConsumeReportView, ConsumeScoreMode } from '../types';
import FilterChip from './FilterChip';
import HelpTip from './HelpTip';
import TbcErrorBanner from '../tbcAnalyzer/components/TbcErrorBanner';
import TbcLogUrlPhase from '../tbcAnalyzer/components/TbcLogUrlPhase';
import TbcPhaseHeader from '../tbcAnalyzer/components/TbcPhaseHeader';
import TbcSelectionPhase from '../tbcAnalyzer/components/TbcSelectionPhase';
import ConsumesGrid, { CONSUME_SCORE_MODES } from '../tbcAnalyzer/components/ConsumesGrid';
import { readStoredString, writeStoredString } from '../tbcAnalyzer/utils';
import { useTbcAnalyzerSession } from '../tbcAnalyzer/useTbcAnalyzerSession';
import { RegeneratedReport, TbcReportRouteProps, useTbcReportRoute } from '../tbcAnalyzer/useTbcReportRoute';
import { decodeReportParams, encodeReportParams, logUrlFromReportCode } from '../tbcAnalyzer/reportParams';
import WclQueryProgress from '../tbcAnalyzer/components/WclQueryProgress';
import LogFilterMenu from '../tbcAnalyzer/components/LogFilterMenu';

const ANALYZE_TIMEOUT_MS = 10 * 60 * 1000;
const ALL_NIGHTS_KEY = 'all';
const ALL_REPORTS_KEY = 'all';
const ALL_RAIDS_KEY = 'all';
const WCL_REPORT_BASE = 'https://fresh.warcraftlogs.com/reports';
const SCORE_MODE_STORAGE_KEY = 'wow_calcs.consumes.scoreMode';

function readStoredScoreMode(): ConsumeScoreMode {
    const stored = readStoredString(SCORE_MODE_STORAGE_KEY);
    return CONSUME_SCORE_MODES.some((entry) => entry.mode === stored) ? (stored as ConsumeScoreMode) : 'relaxed';
}

function nightDateLabel(startMs: number): string {
    return new Date(startMs).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });
}

function raidNightChipLabel(night: ConsumeRaidNight, nights: ConsumeRaidNight[]): string {
    const dateLabel = nightDateLabel(night.startMs);
    const sameDate = nights.filter((other) => nightDateLabel(other.startMs) === dateLabel).length;
    if (sameDate > 1) {
        const time = new Date(night.startMs).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
        });
        return `${dateLabel} · ${time}`;
    }
    return dateLabel;
}

function raidNightRangeLabel(night: ConsumeRaidNight): string {
    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    };
    return `${new Date(night.startMs).toLocaleString(undefined, options)} – ${new Date(night.endMs).toLocaleString(
        undefined,
        options
    )}`;
}

function reportChipLabel(report: ConsumeReportView, reports: ConsumeReportView[]): string {
    const title = report.title.trim();
    const looksLikeId = !title || title === report.id;
    const dateLabel = report.startMs > 0 ? nightDateLabel(report.startMs) : '';
    if (looksLikeId) {
        return dateLabel || report.id;
    }
    const sameTitle = reports.filter((other) => other.title.trim() === title).length;
    if (sameTitle > 1 && dateLabel) {
        return `${title} · ${dateLabel}`;
    }
    return title.length > 36 ? `${title.slice(0, 34)}…` : title;
}

function fallbackReports(result: ConsumeCheckData, loadedLogUrls: string[]): ConsumeReportView[] {
    if (result.reports && result.reports.length > 0) {
        return result.reports;
    }
    return result.reportIds.map((id) => {
        const match = loadedLogUrls.find((url) => url.includes(id));
        const url = match ? match.split(/[?#]/)[0] : `${WCL_REPORT_BASE}/${id}`;
        return {
            id,
            title: id,
            url,
            startMs: 0,
            groupBy: result.groupBy,
            columns: result.columns,
            players: result.players,
            raids: result.raids,
        };
    });
}

const ConsumesOverviewHelp: React.FC = () => (
    <HelpTip label="How the consume checker works" wide>
        <h4>What this shows</h4>
        <p>A raid consume grid from the Warcraft Logs you loaded.</p>
        <ul>
            <li>
                One column per boss. If you load several raids (Hyjal, Black Temple, …), you get one column per raid
                instead. Raid tags above the table zoom back to the bosses for one instance.
            </li>
            <li>Grey icon = that slot was missing. Colored icon = a valid consume for their spec.</li>
            <li>If the same pull is in more than one log, only one copy is kept.</li>
        </ul>
    </HelpTip>
);

const ConsumesResultsHelp: React.FC<{
    filterLabel: string;
    duplicatesRemoved: number;
}> = ({ filterLabel, duplicatesRemoved }) => (
    <HelpTip label="How to read the consume grid" wide>
        <h4>Slots that affect score</h4>
        <ul>
            <li>Battle elixir</li>
            <li>Guardian elixir (a flask fills both elixir slots)</li>
            <li>Food</li>
            <li>Weapon oil / imbue</li>
            <li>Combat potion</li>
        </ul>

        <h4>Score</h4>
        <ul>
            <li>Average of those five slots on every pull the player was there for (Standard mode).</li>
            <li>Best consume for their spec = full credit. Any other valid consume = half.</li>
            <li>Extra icons are shown but do not change score, except in Very sweaty mode.</li>
            <li>Relaxed and Very sweaty modes change these rules; switch above the grid.</li>
            <li>
                Spec comes from Warcraft Logs for that fight. If it is missing, we use their most common spec in that
                log.
            </li>
            <li>Potions: uses vs how many they could have used (2 min cooldown).</li>
            <li>Rune, sapper, drums, and Kreeg&apos;s / Mistletoe icons show uses only.</li>
            <li>Players need at least one boss kill to appear. Trash-only raiders are hidden.</li>
            <li>Rows are grouped tank / healer / melee / caster, then sorted by score.</li>
        </ul>

        <h4>Extra consumes</h4>
        <p>Turn on Extra consumes above the table to show:</p>
        <ul>
            <li>Super Sapper Charge and Drums of Battle for everyone (icon only if they used them)</li>
            <li>Dark Rune / Demonic Rune for everyone (icon only if they used them)</li>
            <li>Thistle Tea for rogues (same extra slot as runes)</li>
            <li>Scroll of Agility and Scroll of Strength for melee</li>
            <li>Scroll of Spirit for mana users</li>
            <li>Kreeg&apos;s / Mistletoe for resto druids and holy/disc priests (uses only; not scored or billed)</li>
        </ul>

        <h4>Gold</h4>
        <p>
            Dreamscythe Alliance auction house, using TSM min buyout. Bought extras are included. Kreeg&apos;s and
            Mistletoe are not. Hover a name or the gold amount for the itemized list.
        </p>

        <h4>Icons and columns</h4>
        <ul>
            <li>When a column covers several pulls, each icon is fights with a valid consume / pulls attended.</li>
            <li>Hover the score for the per-slot breakdown, or an icon for details.</li>
            <li>
                Att. is boss attempts they were in / bosses in this view. Duplicate pulls across logs are counted once.
            </li>
            <li>
                Report tags above the table link out to Warcraft Logs. If several reports are loaded, click a tag to
                zoom to that log. Date tags zoom to one raid night (one stretch of combat, including past midnight).
                Raid tags zoom to one instance (Hyjal, Black Temple, and so on).
            </li>
        </ul>

        {filterLabel && (
            <>
                <h4>This run</h4>
                <p>{filterLabel}</p>
            </>
        )}
        {duplicatesRemoved > 0 && (
            <p>
                Dropped {duplicatesRemoved} duplicate pull{duplicatesRemoved === 1 ? '' : 's'} that appeared in more
                than one log.
            </p>
        )}
    </HelpTip>
);

const ConsumesChecker: React.FC<TbcReportRouteProps> = ({ basePath, reportId }) => {
    const session = useTbcAnalyzerSession({ roleFilter: 'all' });
    const [result, setResult] = useState<ConsumeCheckData | null>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    /** POST the analysis with progress tracking; null on failure (error is set). */
    const requestConsumes = async (body: typeof session.sharedRequestBody): Promise<ConsumeCheckData | null> => {
        session.setLoading(true);
        session.setError(null);
        const wclProgressId = session.beginWclProgress();
        try {
            const response = await axios.post(
                '/api/attendance/analyze-consumes',
                { ...body, wclProgressId },
                {
                    timeout: ANALYZE_TIMEOUT_MS,
                }
            );
            return response.data;
        } catch (err: any) {
            console.error('Error analyzing consumes:', err);
            session.setError(
                err.response?.data?.error ||
                    err.response?.data?.details ||
                    'Failed to analyze consumes. Please try again.'
            );
            return null;
        } finally {
            session.endWclProgress();
            session.setLoading(false);
        }
    };

    /** Rebuild a shared report from the parameters encoded in its id. */
    const regenerateReport = async (key: string): Promise<RegeneratedReport<ConsumeCheckData, undefined> | null> => {
        const params = decodeReportParams(key);
        if (!params) {
            session.setError('That report link is not valid.');
            return null;
        }
        const logUrls = params.reportCodes.map(logUrlFromReportCode);
        session.setLogUrlsText(logUrls.join('\n'));
        const loaded = await session.fetchLogs(logUrls);
        if (!loaded) {
            return null;
        }
        const selectedEncounterIds =
            params.encounterIds.length > 0 ? params.encounterIds : loaded.bosses.map((boss) => boss.encounterId);
        const minDurationSeconds = params.minDurationSeconds > 0 ? String(params.minDurationSeconds) : '';
        session.setFightFilter(params.fightFilter);
        session.setSelectedEncounterIds(selectedEncounterIds);
        session.setMinDurationSeconds(minDurationSeconds);

        const data = await requestConsumes({
            logUrls,
            selectedPlayerName: '',
            fightFilter: params.fightFilter,
            encounterIds: params.encounterIds,
            minDurationSeconds: params.minDurationSeconds,
            dedupeFightsAcrossLogs: logUrls.length > 1,
        });
        if (!data) {
            // Logs are loaded: leave the user on selection to retry.
            session.setPhase('selection');
            return null;
        }
        return {
            result: data,
            snapshot: {
                players: loaded.players,
                bosses: loaded.bosses,
                loadedLogUrls: logUrls,
                selectedPlayerName: loaded.players[0]?.name ?? '',
                fightFilter: params.fightFilter,
                selectedEncounterIds,
                minDurationSeconds,
            },
        };
    };

    const reportRoute = useTbcReportRoute<ConsumeCheckData>({
        basePath,
        reportId,
        session,
        onRestore: (restored) => {
            setResult(restored);
        },
        onLeave: () => {
            setResult(null);
        },
        regenerate: regenerateReport,
    });
    const [nightKey, setNightKey] = useState(ALL_NIGHTS_KEY);
    const [reportKey, setReportKey] = useState(ALL_REPORTS_KEY);
    const [raidKey, setRaidKey] = useState(ALL_RAIDS_KEY);
    const [showExtras, setShowExtras] = useState(false);
    const [scoreMode, setScoreMode] = useState<ConsumeScoreMode>(readStoredScoreMode);

    const changeScoreMode = (mode: ConsumeScoreMode) => {
        setScoreMode(mode);
        writeStoredString(SCORE_MODE_STORAGE_KEY, mode);
    };

    useEffect(() => {
        setNightKey(ALL_NIGHTS_KEY);
        setReportKey(ALL_REPORTS_KEY);
        setRaidKey(ALL_RAIDS_KEY);
    }, [result]);

    const handleProcessAnalysis = async () => {
        if (session.loadedLogUrls.length === 0) {
            session.setError('No logs loaded');
            return;
        }
        if (session.bosses.length > 0 && session.selectedEncounterIds.length === 0) {
            session.setError('Please select at least one boss');
            return;
        }

        setResult(null);
        const body = session.sharedRequestBody;
        const data = await requestConsumes(body);
        if (!data) {
            return;
        }
        setResult(data);
        session.setPhase('results');
        const shareKey = encodeReportParams(body.logUrls, {
            fightFilter: body.fightFilter,
            encounterIds: body.encounterIds,
            minDurationSeconds: body.minDurationSeconds,
        });
        reportRoute.publish(data, undefined, shareKey ?? undefined);
    };

    const handleBackToUrl = () => {
        session.handleBackToUrl();
        setResult(null);
    };

    const reports = result ? fallbackReports(result, session.loadedLogUrls) : [];
    const singleReport = reports.length === 1 ? reports[0] : null;
    const effectiveReportKey = singleReport ? singleReport.id : reportKey;

    const nights = useMemo(() => {
        const allNights = result?.nights ?? [];
        if (effectiveReportKey === ALL_REPORTS_KEY) {
            return allNights;
        }
        return allNights.filter((night) => (night.reportIds ?? []).includes(effectiveReportKey));
    }, [result, effectiveReportKey]);

    const selectedNight = nights.find((night) => night.key === nightKey);
    const selectedReport = reports.find((report) => report.id === effectiveReportKey);
    const raids = useMemo((): ConsumeRaidView[] => {
        if (selectedNight) {
            return selectedNight.raids ?? [];
        }
        if (effectiveReportKey !== ALL_REPORTS_KEY && selectedReport) {
            return selectedReport.raids ?? [];
        }
        return result?.raids ?? [];
    }, [selectedNight, effectiveReportKey, selectedReport, result]);
    const selectedRaid = raids.find((raid) => raid.key === raidKey);
    const viewingNight = Boolean(selectedNight);
    const viewColumns =
        selectedRaid?.columns ?? selectedNight?.columns ?? selectedReport?.columns ?? result?.columns ?? [];
    const viewPlayers =
        selectedRaid?.players ?? selectedNight?.players ?? selectedReport?.players ?? result?.players ?? [];
    const scoreModesAvailable = viewPlayers.every((player) => player.scoreModes != null);
    const viewGroupBy = selectedRaid?.groupBy ?? selectedNight?.groupBy ?? selectedReport?.groupBy ?? result?.groupBy;
    const columnLabel = result
        ? viewGroupBy === 'raid'
            ? `${viewColumns.length} raid${viewColumns.length === 1 ? '' : 's'}`
            : `${viewColumns.length} boss${viewColumns.length === 1 ? '' : 'es'}`
        : '';

    const selectReport = (nextKey: string) => {
        setReportKey(nextKey);
        setNightKey(ALL_NIGHTS_KEY);
        setRaidKey(ALL_RAIDS_KEY);
    };

    const selectNight = (nextKey: string) => {
        setNightKey(nextKey);
    };

    return (
        <div className="item-set-calculator">
            {session.phase !== 'results' && (
                <h2 className="heading-with-help">
                    Consumable Check
                    <ConsumesOverviewHelp />
                </h2>
            )}
            {session.phase !== 'results' && (
                <p className="analyzer-description">Check raid consumes from the logs you load below.</p>
            )}

            <TbcErrorBanner error={session.error} />

            {reportRoute.regenerating && (
                <div className="analyzer-form">
                    <p className="analyzer-description">Rebuilding this shared report from its logs…</p>
                    {session.loading && <WclQueryProgress snapshot={session.wclSnapshot ?? null} />}
                </div>
            )}

            {session.phase === 'url' && !reportRoute.restoring && (
                <TbcLogUrlPhase
                    logUrlsText={session.logUrlsText}
                    loading={session.loading}
                    loadingLabel="Loading players..."
                    onLogUrlsChange={session.setLogUrlsText}
                    onLoadLogs={() => {
                        void session.handleLoadLogs();
                    }}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'selection' && !reportRoute.restoring && (
                <TbcSelectionPhase
                    loadedLogUrls={session.loadedLogUrls}
                    players={session.players}
                    bosses={session.bosses}
                    fightFilter={session.fightFilter}
                    selectedEncounterIds={session.selectedEncounterIds}
                    minDurationSeconds={session.minDurationSeconds}
                    loading={session.loading}
                    analyzeButtonLabel="Check Consumes"
                    hidePlayerPicker
                    hideFightFilter
                    title="Select Bosses"
                    titleHelp={
                        <HelpTip label="Selecting bosses" wide>
                            <p>Consume uses always count the whole log — trash, wipes, and kills.</p>
                            <p>
                                Uncheck bosses to hide their columns. That does not drop consume uses from those fights.
                            </p>
                        </HelpTip>
                    }
                    playerCountLabel="Players found:"
                    showMinDurationFilter
                    minDurationHelpText="Only hides short boss columns. Consume uses still count every fight, including trash."
                    bossDescription={
                        <p className="analyzer-description">
                            Consume uses count every fight, including trash and wipes. Uncheck bosses to hide their
                            columns — that does not drop their consume uses.
                        </p>
                    }
                    onToggleEncounterId={session.toggleEncounterId}
                    onToggleAllEncounterIds={session.toggleAllEncounterIds}
                    onToggleRaidEncounterIds={session.toggleRaidEncounterIds}
                    onMinDurationChange={session.setMinDurationSeconds}
                    onAnalyze={() => {
                        void handleProcessAnalysis();
                    }}
                    onBack={handleBackToUrl}
                    wclSnapshot={session.wclSnapshot}
                />
            )}

            {session.phase === 'results' && result && (
                <div className="results-phase">
                    <TbcPhaseHeader
                        exportTarget={resultsRef}
                        shareable
                        title="Consumable Check"
                        backLabel="← Back to Selection"
                        onBack={reportRoute.leave}
                        help={
                            <ConsumesResultsHelp
                                filterLabel={result.fightFilterLabel}
                                duplicatesRemoved={result.duplicatesRemoved ?? 0}
                            />
                        }
                    />

                    {(reports.length > 0 || nights.length > 1 || raids.length > 1) && (
                        <div className="filter-chip-rows">
                            {reports.length > 0 && (
                                <div className="filter-chip-row" data-label="Logs">
                                    <LogFilterMenu
                                        logs={reports.map((report) => ({
                                            id: report.id,
                                            label: reportChipLabel(report, reports),
                                            url: report.url,
                                        }))}
                                        selectedId={
                                            !viewingNight && effectiveReportKey !== ALL_REPORTS_KEY
                                                ? effectiveReportKey
                                                : null
                                        }
                                        highlightedIds={viewingNight ? (selectedNight?.reportIds ?? []) : []}
                                        onSelect={(id) => {
                                            selectReport(id ?? ALL_REPORTS_KEY);
                                        }}
                                    />
                                </div>
                            )}

                            {nights.length > 1 && (
                                <div
                                    className="filter-chip-row"
                                    data-label="Nights"
                                    role="tablist"
                                    aria-label="Raid nights"
                                >
                                    <FilterChip
                                        label="All nights"
                                        active={nightKey === ALL_NIGHTS_KEY}
                                        onClick={() => {
                                            selectNight(ALL_NIGHTS_KEY);
                                        }}
                                    />
                                    {nights.map((night) => (
                                        <FilterChip
                                            key={night.key}
                                            label={raidNightChipLabel(night, nights)}
                                            active={nightKey === night.key}
                                            title={raidNightRangeLabel(night)}
                                            onClick={() => {
                                                selectNight(night.key);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}

                            {raids.length > 1 && (
                                <div
                                    className="filter-chip-row"
                                    data-label="Raids"
                                    role="tablist"
                                    aria-label="Raid instances"
                                >
                                    <FilterChip
                                        label="All raids"
                                        active={raidKey === ALL_RAIDS_KEY}
                                        onClick={() => {
                                            setRaidKey(ALL_RAIDS_KEY);
                                        }}
                                    />
                                    {raids.map((raid) => (
                                        <FilterChip
                                            key={raid.key}
                                            label={raid.label}
                                            active={raidKey === raid.key}
                                            onClick={() => {
                                                setRaidKey(raid.key);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {viewPlayers.length === 0 || viewColumns.length === 0 ? (
                        <p className="results-status">No matching raid members or boss fights in this filter.</p>
                    ) : (
                        <>
                            <div className="consume-toolbar">
                                <span className="toolbar-label">Score</span>
                                <div className="chip-segment" role="group" aria-label="Score mode">
                                    {CONSUME_SCORE_MODES.map((entry) => (
                                        <FilterChip
                                            key={entry.mode}
                                            label={entry.label}
                                            title={
                                                scoreModesAvailable
                                                    ? entry.help
                                                    : 'This result has no per-mode scores (made by an older backend). Re-run the analysis.'
                                            }
                                            locked={!scoreModesAvailable}
                                            active={
                                                scoreModesAvailable
                                                    ? scoreMode === entry.mode
                                                    : entry.mode === 'standard'
                                            }
                                            onClick={() => {
                                                changeScoreMode(entry.mode);
                                            }}
                                        />
                                    ))}
                                </div>
                                <HelpTip label="Score modes">
                                    {CONSUME_SCORE_MODES.map((entry) => (
                                        <p key={entry.mode}>
                                            <strong>{entry.label}:</strong> {entry.help}
                                        </p>
                                    ))}
                                </HelpTip>
                                <span className="toolbar-divider" aria-hidden="true" />
                                <span className="toolbar-label">Show</span>
                                <FilterChip
                                    label="Extra consumes"
                                    active={showExtras}
                                    onClick={() => {
                                        setShowExtras((value) => !value);
                                    }}
                                />
                                <HelpTip label="Extra consumes">
                                    <p>
                                        Optional extras. They show as extra icons and only change the Very sweaty score.
                                    </p>
                                    <ul>
                                        <li>Sappers and Drums of Battle for everyone (icon if used)</li>
                                        <li>Dark Rune / Demonic Rune for everyone (icon if used)</li>
                                        <li>Thistle Tea for rogues (same extra slot as runes)</li>
                                        <li>Agility and Strength scrolls for melee</li>
                                        <li>Spirit scrolls for mana users</li>
                                        <li>Kreeg&apos;s / Mistletoe for resto druids and holy/disc priests</li>
                                    </ul>
                                </HelpTip>
                                <span className="toolbar-status">
                                    {viewPlayers.length} players · {columnLabel} · {result.fightsProcessed} fight
                                    {result.fightsProcessed === 1 ? '' : 's'}
                                </span>
                            </div>
                            <div ref={resultsRef}>
                                <ConsumesGrid
                                    columns={viewColumns}
                                    players={viewPlayers}
                                    tsmRealmLabel={result.tsmRealmLabel}
                                    showExtras={showExtras}
                                    scoreMode={scoreModesAvailable ? scoreMode : 'standard'}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ConsumesChecker;
